import {
  Alert,
  Button,
  Divider,
  Loader,
  Modal,
  Option,
  SelectBox,
  styled,
  TextInput,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useQuery } from "@tanstack/react-query";
import { FullscreenIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useBlocker } from "react-router-dom";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { getPlatform } from "@/hooks/usePlatform";
import LabelsAPI from "@/api/labels/label_api";
import type { OrgInfo } from "@/utils/labels/labelUtils";
import {
  DYMO_PRINTERS_QUERY_KEY,
  listPrinters,
  normalizeCopies,
  renderLabelPreview,
} from "@/utils/labels/dymo";
import type { DymoPrinter } from "@/utils/labels/dymo";
import {
  getLastLabelId,
  resolveDefaultPrinter,
  setLastLabelId,
  setLastPrinterForLabel,
} from "@/utils/labels/labelPreferences";
import {
  BULK_PRINT_SOFT_CAP,
  NO_ANSWER_MESSAGE,
  describeAccepted,
  describeRejected,
  printLabelsAsBatch,
} from "../bulkPrint";
import { buildItemPreviewXml } from "../labelPreview";
import type { Label } from "../labels.types";
import { LabelPreviewZoom } from "./LabelPreviewZoom";
import { PrintLabelsBanner } from "./PrintLabelsBanner";

/**
 * What a run needs besides the items: the caller's lookups (case and client
 * records for evidence, say) and the org info, or why they couldn't load.
 * `prepareRun` never throws; a failure comes back as `ok: false`.
 */
export type PreparedRun<L> =
  | { ok: true; lookups: L; orgInfo: OrgInfo }
  | {
      ok: false;
      /** Whether Try Again can fix it (a failed request) or not (no access). */
      retryable: boolean;
      /** What went wrong, one clause with no trailing period. */
      message: string;
      /** What the user can do instead, shown after the message when not retryable. */
      hint?: string;
      /** Whatever did load, handed back as `previous` on Try Again. */
      lookups?: L;
    };

export type PrintLabelsModalProps<T, L> = {
  /**
   * Callers mount the dialog to open it and unmount it to close it. Each mount
   * is one open, with its own items, defaults and filled preview.
   */
  open: boolean;
  onClose: () => void;
  /** The items to print, in print order. Called once each time the dialog opens. */
  resolveItems: () => Promise<T[]>;
  /** Stable identity for one item, like its uuid. Keys the filled preview. */
  itemKey: (item: T) => string;
  /** Short name for one item, like its evidence number. */
  itemLabel: (item: T) => string;
  /**
   * Loads the lookups for `items` and `labelXml`, and checks every item can be
   * filled in. `previous` is the lookups of this dialog's last failed send on
   * the same template, so Try Again only refetches what failed.
   */
  prepareRun: (
    items: T[],
    labelXml: string,
    previous?: L
  ) => Promise<PreparedRun<L>>;
  /** The `itemInfo` `customizeLabel` fills one item's label from. */
  buildItemInfo: (item: T, lookups: L) => any;
  /** How one item reads in the preview item picker; defaults to `itemLabel`. */
  renderItemOption?: (option: Option) => React.ReactNode;
  /** Shown when hovering an item in the preview item picker. Gets the item as `data`. */
  ItemTooltip?: (props: { data: T }) => React.ReactNode;
};

type Phase = "form" | "sending" | "rejected" | "lookup-failed" | "unknown";

type Failure = {
  message: string;
  retryable: boolean;
  hint?: string;
};

// Bumped every time a dialog opens, and never reset, so an open id stays
// unique across unmount and remount. The filled preview is keyed by it, so a
// reopen after the item's data changed never shows the old image.
let openCounter = 0;

const PREVIEW_STALE_TIME = 5 * 60 * 1000;

/** Drops a navigation attempted while a batch is being sent. */
const holdNavigation = () => {};

/** A preview failure whose message is meant for the user. */
class PreviewError extends Error {}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const withoutTrailingPeriod = (text: string) => text.replace(/[.\s]+$/, "");

const MODAL_STYLE = { width: 760, maxWidth: "calc(100vw - 32px)" };

const PREVIEW_BOX_HEIGHT = 170;

/**
 * A rendered preview box takes the label's shape, so a tall label gets a tall
 * box instead of shrinking to fit a wide one. Between these heights.
 */
const PREVIEW_BOX_MIN_HEIGHT = 120;
const PREVIEW_BOX_MAX_HEIGHT = "min(360px, 40vh)";

// Both columns stretch to the row's height and sit their content at the
// bottom, so the two previews line up even when what's above them differs in
// height (single item mode has a plain label where the picker would be).
const previewColumnStyle = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "flex-end",
  gap: 8,
};

const previewBoxStyle = {
  position: "relative" as const,
  height: PREVIEW_BOX_HEIGHT,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 8,
  borderRadius: 6,
  border: "1px solid var(--border-color, rgba(128,128,128,0.35))",
  fontSize: 12,
  textAlign: "center" as const,
};

// The enlarge button shows up when the preview is hovered (or tabbed into),
// like the buttons on a note's images, and opening it is a click on it.
const StyledPreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .enlarge-button {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    padding: 0;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    background-color: ${({ theme }) => theme.palette.background.paper};
  }

  &:hover .enlarge-button,
  &:focus-within .enlarge-button {
    opacity: 1;
  }
`;

const fieldLabelStyle = { fontSize: 12, fontWeight: 500 };

const emptyStateStyle = { textAlign: "center" as const, padding: "8px 0 12px" };

/**
 * The Print Labels dialog, shared by bulk print (the evidence table's Actions
 * menu) and single item print (the evidence detail page). One item means
 * single item mode: no preview item picker.
 *
 * Item agnostic: the caller hands in how to resolve the items, how to load
 * what their labels need, and how to fill one item's label. The whole run goes
 * to DYMO Connect as one job (see `bulkPrint.ts`), so it's accepted, rejected
 * or unanswered as a whole, and we never retry on our own.
 */
export function PrintLabelsModal<T, L>({
  open,
  onClose,
  resolveItems,
  itemKey,
  itemLabel,
  prepareRun,
  buildItemInfo,
  renderItemOption,
  ItemTooltip,
}: PrintLabelsModalProps<T, L>) {
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  // The label sits on a backdrop with its own edge, like the enlarged view, so
  // the box's spare room never reads as part of the label.
  const boxStyle = {
    ...previewBoxStyle,
    background: theme.palette.background.alt,
    color: theme.palette.text.secondary,
  };

  // Callers mount the dialog to open it, so every open is a fresh mount with
  // its own id.
  const [openId] = useState(() => ++openCounter);
  const [pickedLabelId, setPickedLabelId] = useState<number | undefined>();
  // The printer pick remembers the template it was made for, so picking
  // another template goes back to that template's default printer.
  const [pickedPrinter, setPickedPrinter] = useState<
    { labelId: number | undefined; name: string } | undefined
  >();
  const [copies, setCopies] = useState<string>("1");
  const [phase, setPhase] = useState<Phase>("form");
  const [failure, setFailure] = useState<Failure | undefined>();
  const [previewIndex, setPreviewIndex] = useState(0);
  // Which preview is enlarged, if any. The enlarged view replaces the form.
  const [zoomed, setZoomed] = useState<"template" | "item">();
  // Width over height of the selected template's label, from its render.
  const [labelShape, setLabelShape] = useState<{
    labelId?: number;
    aspect: number;
  }>();

  // Set synchronously when a send starts, so a second click that lands before
  // React re-renders can't start a second job.
  const inFlight = useRef(false);
  // The lookups of the last failed send, reused only by Try Again on the same
  // template.
  const failedRun = useRef<{ lookups: L; labelId: number }>();
  const contentRef = useRef<HTMLDivElement>(null);

  // Items resolve once per open, as the prop promises, even when the caller
  // hands in a new function while the dialog is open (the detail page rebuilds
  // its adapter when its evidence record refetches with changes). Re-resolving
  // then would reset the picker and the count under the user.
  const resolveItemsRef = useRef(resolveItems);
  resolveItemsRef.current = resolveItems;

  // Keyed by the open, so the items resolve once per open, and Retry is a
  // refetch. `staleTime` keeps a window focus from resolving them again.
  const itemsQuery = useQuery<T[]>({
    queryKey: ["print-labels:items", openId],
    queryFn: () => resolveItemsRef.current(),
    enabled: open,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });
  const items = itemsQuery.data;
  const itemsError = !itemsQuery.error
    ? undefined
    : itemsQuery.error instanceof Error && itemsQuery.error.message
    ? itemsQuery.error.message
    : "Couldn't load the selected items.";

  // The api layer resolves a failed request as `{ success: false }`, so the
  // payload is checked here and a failure becomes a query error.
  const {
    data: templatesData,
    isFetched: templatesFetched,
    isFetching: templatesFetching,
    refetch: refetchTemplates,
  } = useQuery<Label[]>({
    queryKey: ["item-labels"],
    queryFn: async () => {
      const res = await LabelsAPI.getLabels();
      if (!Array.isArray(res)) {
        throw new Error("Couldn't load the label templates");
      }
      return res;
    },
    enabled: open,
    retry: false,
  });
  // Other screens share the `["item-labels"]` key without this check, so a
  // cached failure from one of them can still land here.
  const templates = Array.isArray(templatesData) ? templatesData : undefined;

  const {
    data: printers,
    isFetched: printersFetched,
    isError: printersError,
    isFetching: printersFetching,
    refetch: refetchPrinters,
  } = useQuery<DymoPrinter[]>({
    queryKey: DYMO_PRINTERS_QUERY_KEY,
    queryFn: listPrinters,
    enabled: open,
    retry: false,
  });

  // The user's pick while it's listed, else the remembered template, else the
  // first. A template picked from a cached list can be gone from the fresh one
  // (it was deleted in Settings).
  const labelId = useMemo(() => {
    if (!templates || templates.length === 0) return undefined;
    if (
      pickedLabelId !== undefined &&
      templates.some((t) => t.label_id === pickedLabelId)
    ) {
      return pickedLabelId;
    }
    const remembered = getLastLabelId();
    const match = templates.find((t) => t.label_id === remembered);
    return (match ?? templates[0]).label_id;
  }, [templates, pickedLabelId]);

  // The printer default follows the template. A refreshed printer list (a
  // refetch on window focus, say) keeps the user's pick while it's listed.
  const printerName = useMemo(() => {
    const pick =
      pickedPrinter?.labelId === labelId ? pickedPrinter?.name : undefined;
    return pick && printers?.some((p) => p.name === pick)
      ? pick
      : resolveDefaultPrinter(labelId, printers);
  }, [pickedPrinter, labelId, printers]);

  const itemCount = items?.length ?? 0;

  // Leave guard while the batch is being handed to DYMO Connect. It's a few
  // seconds even for big batches, but leaving mid send unmounts the dialog, so
  // the user never learns whether DYMO took it (and never sees the no answer
  // warning about duplicates). In app navigation (Back included) is held, the
  // same way dismissing is, and `history.block` adds the browser's prompt for
  // a refresh, close or typed URL. Off in Electron, like the upload guard:
  // its main process doesn't handle a renderer's `beforeunload`, so the window
  // would refuse to close or reload with no prompt.
  useBlocker(holdNavigation, phase === "sending" && !getPlatform().isElectron);

  const copiesCount = normalizeCopies(copies);
  const labelCount = itemCount * copiesCount;
  const singleItem = itemCount === 1;
  const selectedTemplate = useMemo(
    () => templates?.find((t) => t.label_id === labelId),
    [templates, labelId]
  );
  const selectedPrinter = useMemo(
    () => printers?.find((p) => p.name === printerName),
    [printers, printerName]
  );
  const previewItem = items?.[previewIndex];

  // Two previews, both rendered by DYMO Connect: the template as it looks in
  // the DYMO Connect editor, and the label as it'll print for the picked item.
  const previewEnabled =
    open && !!selectedTemplate && !printersError && printersFetched;

  const renderTemplate = (printerResolution: boolean) => () => {
    if (!selectedTemplate) throw new Error("Nothing to preview");
    return renderLabelPreview({
      labelXml: selectedTemplate.label_data,
      printerName,
      printerResolution,
    });
  };

  // The preview's lookups live only in this query. A send always prepares its
  // own run over every item, so a preview never decides what prints.
  const renderItem = (printerResolution: boolean) => async () => {
    if (!selectedTemplate || !previewItem) {
      throw new Error("Nothing to preview");
    }
    const prepared = await prepareRun(
      [previewItem],
      selectedTemplate.label_data
    );
    if (!prepared.ok) {
      throw new PreviewError(prepared.message);
    }
    const labelXml = await buildItemPreviewXml(
      selectedTemplate.label_data,
      buildItemInfo(previewItem, prepared.lookups),
      prepared.orgInfo
    );
    return renderLabelPreview({ labelXml, printerName, printerResolution });
  };

  const templateKey = ["dymo:label-preview", "template", labelId, printerName];
  const itemKeyParts = [
    "dymo:label-preview",
    "item",
    labelId,
    printerName,
    previewItem ? itemKey(previewItem) : null,
    openId,
  ];

  const templatePreview = useQuery<string>({
    queryKey: templateKey,
    queryFn: renderTemplate(false),
    enabled: previewEnabled,
    retry: false,
    staleTime: PREVIEW_STALE_TIME,
  });

  const itemPreview = useQuery<string>({
    queryKey: itemKeyParts,
    queryFn: renderItem(false),
    enabled: previewEnabled && !!previewItem,
    retry: false,
    staleTime: PREVIEW_STALE_TIME,
  });

  // The enlarged view renders again at the printer's resolution, so zooming in
  // sharpens small text. Only while that preview is enlarged; it shows the
  // screen resolution image until this one lands, and keeps it if this fails.
  const templateFullPreview = useQuery<string>({
    queryKey: [...templateKey, "printer-resolution"],
    queryFn: renderTemplate(true),
    enabled: previewEnabled && zoomed === "template",
    retry: false,
    staleTime: PREVIEW_STALE_TIME,
  });

  const itemFullPreview = useQuery<string>({
    queryKey: [...itemKeyParts, "printer-resolution"],
    queryFn: renderItem(true),
    enabled: previewEnabled && !!previewItem && zoomed === "item",
    retry: false,
    staleTime: PREVIEW_STALE_TIME,
  });

  const templateOptions: Option[] = (templates || []).map((t) => ({
    label: t.label_name,
    value: t.label_id,
  }));
  const printerOptions: Option[] = (printers || []).map((p) => ({
    label: p.name,
    value: p.name,
  }));
  const previewOptions: Option[] = (items || []).map((item, index) => ({
    label: itemLabel(item),
    value: index,
    data: item,
  }));

  // A failed refetch keeps the last printer list in the cache, so the error
  // has to block printing on its own.
  const canPrint =
    phase === "form" &&
    !!selectedTemplate &&
    !!selectedPrinter &&
    !printersError &&
    itemCount > 0;

  const handleDismiss = () => {
    if (phase === "sending" || inFlight.current) return;
    onClose();
  };

  const send = async () => {
    if (inFlight.current) return;
    inFlight.current = true;

    try {
      const template = selectedTemplate;
      const printer = selectedPrinter;
      const runItems = items;
      if (!template || !printer || !runItems || runItems.length === 0) return;

      const runLabelCount = runItems.length * copiesCount;

      setFailure(undefined);
      // A rejected send's Back should land on the printer picker, not the
      // enlarged preview the user printed from.
      setZoomed(undefined);
      setPhase("sending");

      const previous =
        failedRun.current?.labelId === template.label_id
          ? failedRun.current.lookups
          : undefined;

      const prepared = await prepareRun(
        runItems,
        template.label_data,
        previous
      );

      if (!prepared.ok) {
        failedRun.current = prepared.lookups
          ? { lookups: prepared.lookups, labelId: template.label_id }
          : undefined;
        setFailure({
          message: prepared.message,
          retryable: prepared.retryable,
          hint: prepared.hint,
        });
        setPhase("lookup-failed");
        return;
      }

      failedRun.current = {
        lookups: prepared.lookups,
        labelId: template.label_id,
      };

      const outcome = await printLabelsAsBatch<T>({
        items: runItems,
        labelXml: template.label_data,
        printerName: printer.name,
        printerType: printer.printerType,
        copies: copiesCount,
        buildItemInfo: (item) => buildItemInfo(item, prepared.lookups),
        orgInfo: prepared.orgInfo,
      });

      if (outcome.result === "rejected") {
        setFailure({
          message: describeRejected(outcome.reason),
          retryable: true,
        });
        setPhase("rejected");
        return;
      }
      if (outcome.result === "unknown") {
        setPhase("unknown");
        return;
      }

      failedRun.current = undefined;
      setLastLabelId(template.label_id);
      setLastPrinterForLabel(template.label_id, printer.name);
      enqueueSnackbar(describeAccepted(runLabelCount, printer.name), {
        variant: "success",
      });
      setPhase("form");
      onClose();
    } finally {
      inFlight.current = false;
    }
  };

  const handleBack = () => {
    setFailure(undefined);
    setPhase("form");
  };

  const renderPrinterField = () => {
    if (printersFetching && !printersFetched) {
      return <Loader style={{ margin: "auto" }} />;
    }
    if (printersError) {
      return (
        <div style={emptyStateStyle}>
          <div style={{ fontWeight: 500 }}>Couldn't reach DYMO Connect.</div>
          <div style={{ opacity: 0.75, margin: "4px 0 10px" }}>
            Make sure it's running on this computer, then refresh.
          </div>
          <Button
            size="xs"
            loading={printersFetching}
            onClick={() => refetchPrinters()}
          >
            Refresh
          </Button>
        </div>
      );
    }
    if (printersFetched && printerOptions.length === 0) {
      return (
        <div style={emptyStateStyle}>
          <div style={{ fontWeight: 500 }}>No DYMO printers found.</div>
          <div style={{ opacity: 0.75, margin: "4px 0 10px" }}>
            Connect a DYMO printer and try again.
          </div>
          <Button
            size="xs"
            loading={printersFetching}
            onClick={() => refetchPrinters()}
          >
            Refresh
          </Button>
        </div>
      );
    }
    // monolith-ui's SelectBox decides controlled vs uncontrolled at mount from
    // whether `value` is defined, so it only mounts once the default exists.
    if (printerName === undefined) {
      return <Loader style={{ margin: "auto" }} />;
    }
    return (
      <SelectBox
        label="Printer"
        openOnFocus={false}
        data={printerOptions}
        value={printerName}
        onChange={(_: unknown, option: Option) =>
          setPickedPrinter({ labelId, name: option?.value as string })
        }
      />
    );
  };

  // What a preview shows while there's no image to show, or null once there is.
  const previewPlaceholder = (query: {
    data?: string;
    isError: boolean;
    error: unknown;
  }): React.ReactNode => {
    if (printersError) {
      return "The preview needs DYMO Connect running on this computer.";
    }
    if (query.isError) {
      return query.error instanceof PreviewError
        ? `${withoutTrailingPeriod(query.error.message)}.`
        : "Couldn't render a preview of this label.";
    }
    if (!query.data) return <Loader style={{ margin: "auto" }} />;
    return null;
  };

  const renderPreviewBox = (
    query: { data?: string; isError: boolean; error: unknown },
    alt: string,
    which: "template" | "item"
  ) => {
    if (!selectedTemplate) return null;
    const placeholder = previewPlaceholder(query);
    const aspect =
      labelShape && labelShape.labelId === labelId
        ? labelShape.aspect
        : undefined;
    const shapedBoxStyle = aspect
      ? {
          ...boxStyle,
          height: "auto",
          aspectRatio: aspect,
          minHeight: PREVIEW_BOX_MIN_HEIGHT,
          maxHeight: PREVIEW_BOX_MAX_HEIGHT,
        }
      : boxStyle;
    return (
      <div style={shapedBoxStyle} data-testid={`label-preview-box-${which}`}>
        {placeholder ?? (
          <StyledPreview>
            <img
              src={query.data}
              alt={alt}
              onLoad={(e) => {
                const { naturalWidth, naturalHeight } = e.currentTarget;
                if (naturalWidth > 0 && naturalHeight > 0) {
                  setLabelShape({
                    labelId,
                    aspect: naturalWidth / naturalHeight,
                  });
                }
              }}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
                background: "#fff",
                outline: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Button
              type="button"
              className="enlarge-button"
              size="xxs"
              variant="outlined"
              aria-label={`Enlarge ${alt}`}
              title="Enlarge"
              onClick={() => setZoomed(which)}
            >
              <FullscreenIcon size={14} />
            </Button>
          </StyledPreview>
        )}
      </div>
    );
  };

  const renderItemColumn = () => {
    if (itemsError) {
      return (
        <Alert variant="error" title="Couldn't load the selected items">
          {withoutTrailingPeriod(itemsError)}.{" "}
          <Button
            size="xs"
            variant="subtle"
            loading={itemsQuery.isFetching}
            onClick={() => itemsQuery.refetch()}
          >
            Retry
          </Button>
        </Alert>
      );
    }
    if (!items) {
      return (
        <div style={boxStyle}>
          <Loader style={{ margin: "auto" }} />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div style={boxStyle}>None of the selected items could be found.</div>
      );
    }
    const previewAlt = previewItem
      ? `Label preview for ${itemLabel(previewItem)}`
      : "";
    if (singleItem) {
      return (
        <>
          <div>
            <div style={fieldLabelStyle}>Label preview</div>
            <div style={{ fontSize: 14, marginTop: 4 }}>
              {itemLabel(items[0])}
            </div>
          </div>
          {renderPreviewBox(itemPreview, previewAlt, "item")}
        </>
      );
    }
    return (
      <>
        <SelectBox
          label="Preview item"
          openOnFocus={false}
          searchable
          data={previewOptions}
          value={previewIndex}
          renderOption={
            renderItemOption
              ? (option) =>
                  typeof option === "string" ? option : renderItemOption(option)
              : undefined
          }
          OptionTooltip={ItemTooltip}
          onChange={(_: unknown, option: Option) =>
            setPreviewIndex(Number(option?.value) || 0)
          }
        />
        {renderPreviewBox(itemPreview, previewAlt, "item")}
      </>
    );
  };

  const templateAlt = selectedTemplate
    ? `${selectedTemplate.label_name} template`
    : "";

  const renderZoom = () => {
    if (zoomed === "template") {
      return (
        <LabelPreviewZoom
          title={selectedTemplate?.label_name ?? ""}
          src={templateFullPreview.data ?? templatePreview.data}
          screenSrc={templatePreview.data}
          resetKey="template"
          alt={templateAlt}
          placeholder={previewPlaceholder(templatePreview)}
          onBack={() => setZoomed(undefined)}
        />
      );
    }
    const label = previewItem ? itemLabel(previewItem) : "";
    return (
      <LabelPreviewZoom
        title={label}
        src={itemFullPreview.data ?? itemPreview.data}
        screenSrc={itemPreview.data}
        resetKey={previewIndex}
        alt={label ? `Label preview for ${label}` : ""}
        placeholder={previewPlaceholder(itemPreview)}
        nav={{
          index: previewIndex,
          count: itemCount,
          label,
          onChange: setPreviewIndex,
        }}
        onBack={() => setZoomed(undefined)}
      />
    );
  };

  const renderForm = () => (
    <>
      {!templatesFetched && <Loader style={{ margin: "auto" }} />}
      {templatesFetched && !templates && (
        <Alert variant="error" title="Couldn't load the label templates">
          Check your connection, then retry.{" "}
          <Button
            size="xs"
            variant="subtle"
            loading={templatesFetching}
            onClick={() => refetchTemplates()}
          >
            Retry
          </Button>
        </Alert>
      )}
      {templatesFetched && templates && templateOptions.length === 0 && (
        <Alert variant="info" title="No label templates">
          Upload a label template under Settings before printing.
        </Alert>
      )}
      {/* What prints: the template on the left, this item's label on the right. */}
      {templateOptions.length > 0 && labelId !== undefined && (
        <div style={{ display: "flex", gap: 16 }}>
          <div style={previewColumnStyle}>
            <SelectBox
              label="Label template"
              openOnFocus={false}
              data={templateOptions}
              value={labelId}
              onChange={(_: unknown, option: Option) =>
                setPickedLabelId(option?.value as number)
              }
            />
            {renderPreviewBox(templatePreview, templateAlt, "template")}
          </div>
          <div style={previewColumnStyle}>{renderItemColumn()}</div>
        </div>
      )}
      <Divider style={{ margin: "4px 0" }} />
      {/* How it prints. */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 3, minWidth: 0 }}>{renderPrinterField()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <TextInput
            label="Copies per item"
            type="number"
            min={1}
            value={copies}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCopies(e.target.value)
            }
            onBlur={() => setCopies(String(copiesCount))}
          />
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
            {itemCount} {plural(itemCount, "item", "items")} × {copiesCount} ={" "}
            <strong>
              {labelCount} {plural(labelCount, "label", "labels")}
            </strong>
          </div>
        </div>
      </div>
      {labelCount > BULK_PRINT_SOFT_CAP && (
        <Alert variant="warning" title="That's a lot of labels">
          Make sure the copies count is what you meant before printing. There's
          no limit, this is just a check.
        </Alert>
      )}
    </>
  );

  const renderLookupFailure = () => {
    if (!failure) return null;
    const message = withoutTrailingPeriod(failure.message);
    return (
      <Alert variant="error" title="Couldn't get what these labels need">
        {failure.retryable
          ? `${message}. Nothing was sent to the printer, so it's safe to try again.`
          : `${message}. Nothing was sent to the printer.${
              failure.hint ? ` ${failure.hint}` : ""
            }`}
      </Alert>
    );
  };

  const showTryAgain =
    phase === "rejected" || (phase === "lookup-failed" && !!failure?.retryable);
  // Back returns to the form, so the user can pick another printer after DYMO
  // said no, or another template after a lookup that can't succeed.
  const showBack =
    phase === "rejected" || (phase === "lookup-failed" && !failure?.retryable);

  return (
    // Focus the dialog's content on open. Otherwise the banner's Learn more
    // link is the first tabbable element and takes it. `initialFocus: -1`
    // doesn't work here, since `.mf-Modal` has no tabindex.
    <Modal
      open={open}
      onClose={handleDismiss}
      style={MODAL_STYLE}
      FocusProps={{ initialFocus: contentRef }}
    >
      <Modal.Title>{singleItem ? "Print Label" : "Print Labels"}</Modal.Title>
      <div
        ref={contentRef}
        tabIndex={-1}
        style={{
          marginTop: 16,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          outline: "none",
        }}
      >
        {/* What's new, on the form only so it never sits above a send's outcome. */}
        {phase === "form" && !zoomed && <PrintLabelsBanner />}
        {phase === "form" && !zoomed && renderForm()}
        {phase === "form" && zoomed && renderZoom()}

        {phase === "sending" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              padding: "10px 0",
            }}
          >
            <Loader />
            <div style={{ fontWeight: 500 }}>
              Sending {labelCount} {plural(labelCount, "label", "labels")} to{" "}
              {selectedPrinter?.name || "the printer"}…
            </div>
          </div>
        )}

        {phase === "rejected" && failure && (
          <Alert variant="error">{failure.message}</Alert>
        )}

        {phase === "lookup-failed" && renderLookupFailure()}

        {phase === "unknown" && (
          <Alert variant="warning">{NO_ANSWER_MESSAGE}</Alert>
        )}

        <ButtonMenu>
          {phase !== "sending" && (
            <Button size="xs" variant="subtle" onClick={handleDismiss}>
              {phase === "form" ? "Cancel" : "Close"}
            </Button>
          )}
          {showBack && (
            <Button size="xs" variant="outlined" onClick={handleBack}>
              Back
            </Button>
          )}
          {(phase === "form" || showTryAgain) && (
            <Button
              size="xs"
              color="primary"
              variant="contained"
              onClick={send}
              disabled={phase === "form" && !canPrint}
            >
              {showTryAgain ? "Try Again" : `Print (${labelCount})`}
            </Button>
          )}
        </ButtonMenu>
      </div>
    </Modal>
  );
}
